/**
 * The mobile app's native side, as the dashboard sees it (docs/superpowers/specs/2026-10-30-mobile-app-part1-design.md).
 * The only module that touches Capacitor. In a browser there is none (appBridge() is null) and nothing of
 * the app shows. The dashboard asks which features the app offers instead of assuming them: an older app
 * keeps working with a newer server. In the browser tests a double stands in (window.__velanoxAppDouble),
 * honoured only in the e2e build.
 */
import { Capacitor, registerPlugin } from '@capacitor/core'
import { App } from '@capacitor/app'
import { AppLauncher } from '@capacitor/app-launcher'
import { Browser } from '@capacitor/browser'
import { Device } from '@capacitor/device'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { Network } from '@capacitor/network'
import { Share } from '@capacitor/share'
import { StatusBar, Style } from '@capacitor/status-bar'
import { AccessControl, NativeBiometric } from '@capgo/capacitor-native-biometric'

/** The phone's own Face ID / fingerprint prompt, in the dashboard's language */
export type Prompt = { title: string; cancel: string }

export type PushPermission = 'granted' | 'denied' | 'prompt'

/**
 * An NFC tag as read: its own id (upper-case hex), the first web address written on it, whether it can be
 * written and whether it holds nothing yet
 */
export type NfcRead = { uid: string; uri: string | null; writable: boolean; empty: boolean }
export type Haptic = 'ok' | 'refused'

export interface AppBridge {
  /** scan: the live camera and the app's own storage (the queue); nfc: the NFC plugin (part 3) */
  features: { phone: boolean; files: boolean; browserSignIn: boolean; push?: boolean; scan?: boolean; nfc?: boolean }
  device(): Promise<{ name: string; model: string; platform: 'ios' | 'android' }>
  /** Face ID or a fingerprint set up (the key is kept behind it) */
  canUnlock(): Promise<boolean>
  /** the Face ID / fingerprint / PIN prompt */
  confirm(prompt: Prompt): Promise<boolean>
  /** the phone stored for this server, without a prompt: its id (the key stays behind Face ID) */
  storedPhone(): Promise<string | null>
  /** its key, through the phone's own Face ID / fingerprint prompt; null when cancelled or gone */
  readPhone(prompt: Prompt): Promise<{ id: string; key: string } | null>
  /** kept behind Face ID / a fingerprint; null forgets it */
  savePhone(p: { id: string; key: string } | null, prompt?: Prompt): Promise<void>
  /** a new verifier kept for signInWithBrowser; its challenge (SHA-256, base64url) */
  newChallenge(): Promise<string>
  /**
   * single sign-on in the phone's browser sheet (its own cookies); back with the server's one-time code
   * (a sign-in), `done` (a confirmation, a link) or an error — and the verifier. Nothing: closed by the person.
   */
  signInWithBrowser(url: string): Promise<{ code?: string; done?: string; error?: string; verifier: string }>
  share(file: { name: string; mime: string; data: Blob }): Promise<void>
  openExternal(url: string): Promise<void>
  /** back to the app's start screen (another server) */
  chooseServer(): Promise<void>
  /** Android's back button: true when the dashboard went back itself; the remover back */
  onBack(handler: () => boolean): () => void
  setStatusBar(dark: boolean): void
  /** the remover back */
  onNetwork(handler: (online: boolean) => void): () => void
  // push (part 2; an older app has none: features.push)
  /** whether the phone lets the app show pushes */
  pushPermission?(): Promise<PushPermission>
  /** the phone's own question, the Android channel named; the token, or null when refused */
  allowPush?(channelName: string): Promise<string | null>
  /** the token, only while push is allowed */
  pushToken?(): Promise<string | null>
  /** a push tapped (the app closed, in the background or open): its page; the remover back */
  onPushTap?(handler: (path: string) => void): () => void
  /** Firebase gave the phone a new token; the remover back */
  onPushToken?(handler: (token: string) => void): () => void
  /**
   * a push arriving while the app is open, which the phone does not show itself (Android; an iPhone shows
   * its own banner); the remover back
   */
  onPushReceived?(handler: (push: { title: string; body: string; path: string }) => void): () => void
  /** the phone's push token dropped (Firebase hands out a new one): no server left behind can push to it */
  deletePushToken?(): Promise<void>
  // scanning (part 3; an older app has none: features.scan, features.nfc)
  /** the camera behind the page, every code of each frame to the handler; false: the camera is not allowed */
  startCamera?(onCodes: (codes: string[]) => void): Promise<boolean>
  stopCamera?(): Promise<void>
  hasTorch?(): Promise<boolean>
  torch?(on: boolean): Promise<void>
  /** the phone has NFC */
  nfcReady?(): Promise<boolean>
  /** tags read until stopNfc (Android), or in Apple's sheet; once: the first tag ends it */
  startNfc?(onTag: (t: NfcRead) => void, o: { once: boolean; prompt: string }): Promise<void>
  stopNfc?(): Promise<void>
  /** this address, the only record, onto the tag just read */
  writeNfcUri?(url: string): Promise<void>
  haptic?(kind: Haptic): Promise<void>
  /** the app's own storage (the queue, the remembered events): the dashboard and the app's own pages share it */
  storage?: { get(key: string): Promise<string | null>; set(key: string, value: string): Promise<void> }
}

declare global { interface Window { __velanoxAppDouble?: AppBridge } }

const AppShell = registerPlugin<{ chooseServer(): Promise<void> }>('AppShell')
/** The app's Firebase Messaging plugin (@capacitor-firebase/messaging), reached by name: its native side is the app's */
type Receive = 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale'
const Messaging = registerPlugin<{
  checkPermissions(): Promise<{ receive: Receive }>
  requestPermissions(): Promise<{ receive: Receive }>
  getToken(): Promise<{ token: string }>
  createChannel(o: { id: string; name: string; importance: number }): Promise<void>
  addListener(e: 'notificationActionPerformed', f: (a: { notification: { data?: unknown } }) => void): Promise<{ remove(): Promise<void> }>
  addListener(e: 'tokenReceived', f: (a: { token: string }) => void): Promise<{ remove(): Promise<void> }>
  addListener(e: 'notificationReceived', f: (a: { notification: { title?: string; body?: string; data?: unknown } }) => void): Promise<{ remove(): Promise<void> }>
  deleteToken(): Promise<void>
}>('FirebaseMessaging')
/** The app's barcode, NFC, storage and vibration plugins, reached by name: their native side is the app's */
type Handle = Promise<{ remove(): Promise<void> }>
const Barcodes = registerPlugin<{
  requestPermissions(): Promise<{ camera: string }>
  startScan(o: { formats: string[] }): Promise<void>
  stopScan(): Promise<void>
  addListener(e: 'barcodesScanned', f: (a: { barcodes: { rawValue?: string }[] }) => void): Handle
  isTorchAvailable(): Promise<{ available: boolean }>
  enableTorch(): Promise<void>
  disableTorch(): Promise<void>
}>('BarcodeScanner')
type NdefRecord = { tnf: number; type: number[]; id: number[]; payload: number[] }
/** a tag as the NFC plugin reports it */
type PluginTag = { id?: number[]; isWritable?: boolean | null; techTypes?: string[] | null; ndefMessage?: NdefRecord[] | null }
const Nfc = registerPlugin<{
  isSupported(): Promise<{ supported: boolean }>
  startScanning(o: { invalidateAfterFirstRead?: boolean; alertMessage?: string; iosSessionType?: 'tag' | 'ndef' }): Promise<void>
  stopScanning(): Promise<void>
  write(o: { records: NdefRecord[] }): Promise<void>
  addListener(e: 'nfcEvent', f: (a: { tag: PluginTag }) => void): Handle
}>('CapacitorNfc')
const Preferences = registerPlugin<{ get(o: { key: string }): Promise<{ value: string | null }>; set(o: { key: string; value: string }): Promise<void> }>('Preferences')
const Haptics = registerPlugin<{ impact(o: { style: 'LIGHT' }): Promise<void>; notification(o: { type: 'ERROR' }): Promise<void> }>('Haptics')
/** QR, Code 128, Code 39, EAN-13, EAN-8, UPC-A, UPC-E, Data Matrix */
const FORMATS = ['QR_CODE', 'CODE_128', 'CODE_39', 'EAN_13', 'EAN_8', 'UPC_A', 'UPC_E', 'DATA_MATRIX']
/** an NDEF URI record's prefix codes (NFC Forum): the common ones */
const URI_PREFIX: Record<number, string> = { 0: '', 1: 'http://www.', 2: 'https://www.', 3: 'http://', 4: 'https://' }
const hex = (bytes: number[] = []) => bytes.map(b => (b & 0xff).toString(16).padStart(2, '0')).join('').toUpperCase()
/** the first web address on a tag (a well-known "U" record), or null */
export function uriOf(records: NdefRecord[] | null | undefined): string | null {
  const r = records?.find(x => x.tnf === 1 && x.type.length === 1 && x.type[0] === 0x55 && x.payload.length > 0)
  if (!r || !(r.payload[0] in URI_PREFIX)) return null
  return URI_PREFIX[r.payload[0]] + new TextDecoder().decode(new Uint8Array(r.payload.slice(1)))
}
/**
 * What a tag read names and allows: its own id, the first web address, whether it can be written (an
 * unformatted sticker can: Android's plugin formats it as it writes; an iPhone reports it as neither) and whether
 * it is empty (no record, or only empty ones)
 */
export function nfcReadOf(tag: PluginTag): NfcRead {
  const records = tag.ndefMessage ?? []
  const unformatted = !!tag.techTypes?.includes('android.nfc.tech.NdefFormatable')
  return {
    uid: hex(tag.id), uri: uriOf(records),
    writable: tag.isWritable === true || (tag.isWritable == null && unformatted),
    empty: records.every(r => r.tnf === 0),
  }
}
/** a web address as the only record: https:// as its prefix code, the rest in UTF-8 */
export function uriRecord(url: string): NdefRecord {
  const rest = url.startsWith('https://') ? url.slice(8) : url
  return { tnf: 1, type: [0x55], id: [], payload: [url.startsWith('https://') ? 4 : 0, ...new TextEncoder().encode(rest)] }
}
/** the Android channel pushes come on (the server names it in every push) */
export const PUSH_CHANNEL = 'alerts'
const permissionOf = (r: Receive): PushPermission => r === 'granted' ? 'granted' : r === 'denied' ? 'denied' : 'prompt'
const b64url = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
const blobBase64 = (blob: Blob) => new Promise<string>((resolve, reject) => {
  const r = new FileReader()
  r.onload = () => resolve(String(r.result).split(',')[1] ?? '')
  r.onerror = () => reject(r.error)
  r.readAsDataURL(blob)
})
/** where the phone keeps this server's key (one per installation); its id, which is no secret, beside it */
const keychain = () => ({ server: location.origin })
const PHONE_ID = 'velanox.phone'
/** a native listener's remover */
const remover = (h: Promise<{ remove(): Promise<void> }>) => () => { void h.then(l => l.remove()).catch(() => undefined) }

function nativeBridge(): AppBridge {
  const has = (name: string) => Capacitor.isPluginAvailable(name)
  let verifier = ''
  return {
    features: {
      phone: has('NativeBiometric'), files: has('Filesystem') && has('Share'), browserSignIn: has('Browser') && has('App'), push: has('FirebaseMessaging'),
      scan: has('BarcodeScanner') && has('Preferences'), nfc: has('CapacitorNfc'),
    },
    async device() {
      const info = await Device.getInfo()
      return { name: info.name || info.model, model: info.model, platform: info.platform === 'android' ? 'android' : 'ios' }
    },
    async canUnlock() {
      // the key's storage opens with biometry only: a phone with just a PIN is not added
      try { return (await NativeBiometric.isAvailable({ useFallback: false })).isAvailable } catch { return false }
    },
    async confirm({ title }) {
      try { await NativeBiometric.verifyIdentity({ reason: title, title, useFallback: true }); return true } catch { return false }
    },
    async storedPhone() {
      try {
        const id = localStorage.getItem(PHONE_ID)
        return id && (await NativeBiometric.isCredentialsSaved(keychain())).isSaved ? id : null
      } catch { return null }
    },
    async readPhone({ title, cancel }) {
      try {
        // the phone's secure storage opens it only after Face ID / a fingerprint (its own prompt)
        const c = await NativeBiometric.getSecureCredentials({ ...keychain(), reason: title, title, negativeButtonText: cancel })
        return c.username && c.password ? { id: c.username, key: c.password } : null
      } catch { return null }
    },
    async savePhone(p, prompt) {
      if (!p) {
        localStorage.removeItem(PHONE_ID)
        await NativeBiometric.deleteCredentials(keychain()).catch(() => undefined)
        return
      }
      // hardware-protected; on Android the prompt just shown covers storing the new key (30 s)
      await NativeBiometric.setCredentials({
        ...keychain(), username: p.id, password: p.key, accessControl: AccessControl.BIOMETRY_ANY, authValidityDuration: 30,
        ...(prompt ? { title: prompt.title, negativeButtonText: prompt.cancel } : {}),
      })
      localStorage.setItem(PHONE_ID, p.id)
    },
    async newChallenge() {
      verifier = b64url(crypto.getRandomValues(new Uint8Array(32)))
      return b64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))))
    },
    signInWithBrowser(url) {
      return new Promise(resolve => {
        let done = false
        const finish = (r: { code?: string; done?: string; error?: string }) => {
          if (done) return
          done = true
          void opened.then(h => h.remove()); void closed.then(h => h.remove())
          resolve({ ...r, verifier })
        }
        const opened = App.addListener('appUrlOpen', ({ url: back }) => {
          if (!back.startsWith('velanox://signed-in')) return
          const q = new URL(back).searchParams
          void Browser.close().catch(() => undefined)
          finish({ code: q.get('code') ?? undefined, done: q.get('done') ?? undefined, error: q.get('error') ?? undefined })
        })
        // closed by the person: no sign-in, no error to show
        const closed = Browser.addListener('browserFinished', () => finish({}))
        void Browser.open({ url })
      })
    },
    async share(file) {
      // one file in the cache, whatever the name holds (a site called "Stage A/B")
      const written = await Filesystem.writeFile({ path: file.name.replace(/[\\/]/g, '-'), data: await blobBase64(file.data), directory: Directory.Cache })
      await Share.share({ title: file.name, files: [written.uri] })
    },
    async openExternal(url) { await AppLauncher.openUrl({ url }) },
    async chooseServer() { await AppShell.chooseServer() },
    onBack(handler) { return remover(App.addListener('backButton', () => { if (!handler()) void App.exitApp() })) },
    setStatusBar(dark) { void StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }).catch(() => undefined) },
    onNetwork(handler) {
      void Network.getStatus().then(s => handler(s.connected))
      return remover(Network.addListener('networkStatusChange', s => handler(s.connected)))
    },
    async pushPermission() {
      try { return permissionOf((await Messaging.checkPermissions()).receive) } catch { return 'denied' }
    },
    async allowPush(channelName) {
      try {
        if (permissionOf((await Messaging.requestPermissions()).receive) !== 'granted') return null
        if (Capacitor.getPlatform() === 'android') await Messaging.createChannel({ id: PUSH_CHANNEL, name: channelName, importance: 4 }).catch(() => undefined)
        return (await Messaging.getToken()).token || null
      } catch { return null }
    },
    async pushToken() {
      try {
        if (permissionOf((await Messaging.checkPermissions()).receive) !== 'granted') return null
        return (await Messaging.getToken()).token || null
      } catch { return null }
    },
    onPushTap(handler) {
      return remover(Messaging.addListener('notificationActionPerformed', a => {
        const path = (a.notification.data as { path?: unknown } | undefined)?.path
        if (typeof path === 'string') handler(path)
      }))
    },
    onPushToken(handler) { return remover(Messaging.addListener('tokenReceived', a => handler(a.token))) },
    onPushReceived(handler) {
      // an iPhone shows a push itself with the app open (presentationOptions): only Android hands it over
      if (Capacitor.getPlatform() !== 'android') return () => undefined
      return remover(Messaging.addListener('notificationReceived', a => {
        const n = a.notification, path = (n.data as { path?: unknown } | undefined)?.path
        handler({ title: n.title ?? '', body: n.body ?? '', path: typeof path === 'string' ? path : '' })
      }))
    },
    async deletePushToken() { await Messaging.deleteToken().catch(() => undefined) },
    async startCamera(onCodes) {
      try {
        const { camera } = await Barcodes.requestPermissions()
        if (camera !== 'granted' && camera !== 'limited') return false
        await scanning?.then(h => h.remove()).catch(() => undefined)
        scanning = Barcodes.addListener('barcodesScanned', a => {
          const codes = a.barcodes.map(b => b.rawValue ?? '').filter(Boolean)
          if (codes.length) onCodes(codes)
        })
        await Barcodes.startScan({ formats: FORMATS })
        return true
      } catch { return false }
    },
    async stopCamera() {
      await scanning?.then(h => h.remove()).catch(() => undefined)
      scanning = null
      await Barcodes.stopScan().catch(() => undefined)
    },
    async hasTorch() { try { return (await Barcodes.isTorchAvailable()).available } catch { return false } },
    async torch(on) { await (on ? Barcodes.enableTorch() : Barcodes.disableTorch()).catch(() => undefined) },
    async nfcReady() { try { return (await Nfc.isSupported()).supported } catch { return false } },
    async startNfc(onTag, { once, prompt }) {
      await reading?.then(h => h.remove()).catch(() => undefined)
      reading = Nfc.addListener('nfcEvent', ({ tag }) => onTag(nfcReadOf(tag)))
      // the iPhone's sheet reads tags without NDEF too (their own id) with a tag session
      await Nfc.startScanning({ invalidateAfterFirstRead: once, alertMessage: prompt, iosSessionType: 'tag' })
    },
    async stopNfc() {
      await reading?.then(h => h.remove()).catch(() => undefined)
      reading = null
      await Nfc.stopScanning().catch(() => undefined)
    },
    async writeNfcUri(url) { await Nfc.write({ records: [uriRecord(url)] }) },
    async haptic(kind) { await (kind === 'ok' ? Haptics.impact({ style: 'LIGHT' }) : Haptics.notification({ type: 'ERROR' })).catch(() => undefined) },
    storage: {
      async get(key) { try { return (await Preferences.get({ key })).value } catch { return null } },
      async set(key, value) { await Preferences.set({ key, value }) },
    },
  }
}
/**
 * The phone has one camera and one NFC reader, opened in turns: an opening closed while its start was still on
 * its way is stopped again once it has started, unless another opening has taken it since (a view closed and
 * opened again quickly). started: what the start gave, or null when closed meanwhile
 */
function turns() {
  let turn = 0
  return <T>(start: () => Promise<T> | undefined, stop: () => void) => {
    ++turn
    let closedAt = 0
    const started = Promise.resolve(start()).then(r => {
      if (!closedAt) return r
      if (turn === closedAt) stop()
      return null
    })
    return { started, close() { closedAt = ++turn; stop() } }
  }
}
export const cameraTurn = turns()
export const nfcTurn = turns()

/** the camera's and NFC's listeners while they run */
let scanning: Handle | null = null
let reading: Handle | null = null

let bridge: AppBridge | null | undefined
/** The app's native side; null in a browser */
export function appBridge(): AppBridge | null {
  if (bridge !== undefined) return bridge
  if (import.meta.env.VITE_APP_DOUBLE === '1' && window.__velanoxAppDouble) return (bridge = window.__velanoxAppDouble)
  return (bridge = Capacitor.isNativePlatform() ? nativeBridge() : null)
}

export const inApp = () => appBridge() !== null
